import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

// Wraps App for layout="floating", owning the panel's drag-resizable width
// (height stays CSS-driven — see widget.scss).

const WIDTH_STORAGE_KEY = 'ask-jumino-panel-width';
const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 320;

/** Exported for testing. Keeps the panel from crossing its own 20px right
 * margin (so it never overlaps the left edge of the screen) or getting so
 * narrow the composer/buttons stop fitting. */
export function clampWidth(width: number, viewportWidth: number): number {
  const maxWidth = viewportWidth - 40; // 20px margin on the right + 20px on the left
  return Math.min(Math.max(width, MIN_WIDTH), Math.max(maxWidth, MIN_WIDTH));
}

function readStoredWidth(): number | null {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null; // private mode / quota — fall back to the default
  }
}

function writeStoredWidth(width: number): void {
  try {
    localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
  } catch {
    // ignore — the in-memory width for this session still works
  }
}

export function FloatingPanel({ children }: { children: ComponentChildren }) {
  const [width, setWidth] = useState<number>(() =>
    clampWidth(readStoredWidth() ?? DEFAULT_WIDTH, window.innerWidth),
  );
  // Refs (not state) so the window listeners don't get torn down/re-attached mid-drag.
  const widthRef = useRef(width);
  widthRef.current = width;
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    function onPointerMove(e: PointerEvent): void {
      const drag = dragRef.current;
      if (!drag) return;
      // Right-anchored panel: dragging left (smaller clientX) widens it.
      const delta = drag.startX - e.clientX;
      setWidth(clampWidth(drag.startWidth + delta, window.innerWidth));
    }
    function onPointerUp(): void {
      if (!dragRef.current) return;
      dragRef.current = null;
      writeStoredWidth(widthRef.current);
    }
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  const onHandlePointerDown = (e: PointerEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: widthRef.current };
  };

  return (
    <div class="aj-floating-panel" style={{ width: `${width}px` }}>
      <div
        class="aj-floating-panel__resize-handle"
        title="Drag to resize"
        onPointerDown={onHandlePointerDown}
      />
      {children}
    </div>
  );
}
